import download from 'downloadjs';

import { FileType } from '../types/common.types';
import { Programme, ProgrammeSummary } from '../types/entity.types';
import { ParentProgrammeGeneralDetails } from '../types/entity.types';

import { SHORT_MONTHS } from './constants';

export const noOp = () => {};

export const getClassNames = (baseClassName: string, modifiers: Record<string, unknown>) => {
  const classes = [baseClassName];

  Object.entries(modifiers).forEach(([name, active]) => {
    if (!active) return;

    classes.push(`${baseClassName}--${name}`);
  });

  return classes.join(' ');
};

export const removeCamelCase = (string: string) => {
  let copy = string.repeat(1);
  copy = copy
    .replace(/([A-Z]+)/g, ' $1')
    .replace(/([A-Z][a-z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase());
  return copy;
};

export const isEmpty = (value: string | unknown[] | null | undefined) => !value || value.length === 0;

export const mapStringOptionsToObjectOptions = (stringOptions: readonly string[] | string[]) => {
  return stringOptions.map((string) => ({
    label: string,
    value: string
  }));
};

export const openInNewTab = (url: string | undefined) => {
  if (!url) return;

  let copyOfUrl = url.repeat(1);

  if (!url.startsWith('http')) {
    copyOfUrl = `http://${url}`;
  }

  window.open(copyOfUrl, '_blank', 'noopener, noreferrer');
};

export const isNumeric = (string: unknown) => {
  if (typeof string != 'string') return false;
  return !isNaN(string as unknown as number) && !isNaN(parseFloat(string));
};

export const generateUUID = () => crypto.randomUUID();

export const saveToLocalStorage = (key: string, value: unknown) => localStorage.setItem(key, JSON.stringify(value));

export const retrieveFromLocalStorage = (key: string) => JSON.parse(localStorage.getItem(key)!);

export const downloadFile = (data: BlobPart, name: string) => {
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(new Blob([data]));
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadExcelFile = (base64String: string, name: string) => download(window.atob(base64String), name);

export const fileToBase64 = async (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => resolve((reader.result as string).split('base64,')[1] as string);
    reader.onerror = (error) => reject(error);
  });

export const extractWordAtCaretPosition = (
  string: string | null,
  caretPosition: number | undefined
): [string, number | undefined, number | undefined] => {
  if (!string || caretPosition === undefined) return ['', undefined, undefined];

  let wordStartPosition = caretPosition;
  let wordEndPosition: number | undefined;

  if (string.charAt(caretPosition) === ' ' || string.charAt(caretPosition) === '') {
    wordEndPosition = caretPosition;
  } else {
    for (let i = caretPosition; i <= string.length; i++) {
      wordEndPosition = i;
      if (string.charAt(i) === ' ') {
        break;
      }
    }
  }

  let hasRunOnce = false;

  for (let i = caretPosition; i >= 0; i--) {
    if (hasRunOnce) {
      if (string.charAt(i) === ' ') {
        break;
      }
    }

    wordStartPosition = i;
    hasRunOnce = true;
  }

  const word = string.slice(wordStartPosition, wordEndPosition);

  return [word, wordStartPosition, wordEndPosition];
};

export const replaceWordAtCaretPosition = (string: string, wordToInsert: string, caretPosition: number | undefined) => {
  if (!caretPosition) return string;

  const [wordToReplace, wordStartPosition] = extractWordAtCaretPosition(string, caretPosition);

  const stringArray = Array.from(string);

  stringArray.splice(wordStartPosition as number, (wordToReplace as string).length, ...Array.from(wordToInsert));

  return stringArray.join('');
};

export const isHashtag = (string: string) => {
  if (string.includes(' ')) return false;
  if (string.charAt(0) === '#') return true;
  return false;
};

export const isMention = (string: string) => {
  if (string.includes(' ')) return false;
  if (string.charAt(0) === '@') return true;
  return false;
};

export const convertTimeStringToDate = (timeString: string | null | undefined) => {
  if (!timeString) {
    throw new Error('Please provide a time string');
  }

  const [hours, minutes] = timeString.split(':');

  const date = new Date();
  date.setUTCHours(parseInt(hours!, 10));
  date.setUTCMinutes(parseInt(minutes!, 10));
  date.setUTCSeconds(0);
  date.setUTCMilliseconds(0);

  return date;
};

export const convertDateToTimeString = (date: Date | string) => {
  const dateToFormat = new Date(date);

  let hours: string | number = dateToFormat.getUTCHours();
  let minutes: string | number = dateToFormat.getUTCMinutes();

  if (hours < 10) {
    hours = `0${hours}`;
  }

  if (minutes < 10) {
    minutes = `0${minutes}`;
  }

  return `${hours}:${minutes}`;
};

export const isProgrammeIncomplete = (programme: Programme | ProgrammeSummary | null | undefined) => {
  if (!programme) return true;

  if ('subProgrammeSummaries' in programme) {
    const { planComplete, accountabilityComplete, supportComplete, tribeComplete } = programme;

    if (!planComplete || !accountabilityComplete || !supportComplete || !tribeComplete) {
      return true;
    }
  } else {
    const { planExternalReference, accountabilityExists, supportExternalReference, tribeExternalReference } = programme;

    if (!planExternalReference || !accountabilityExists || !supportExternalReference || !tribeExternalReference) {
      return true;
    }
  }

  return false;
};

export const base64ToImage = (base64String: string | null | undefined, imageType = 'jpeg') => {
  if (!base64String) return undefined;

  return `data:image/${imageType};base64,${base64String}`;
};

export const fileToImage = (file: File) => {
  return URL.createObjectURL(file);
};

export const isImage = (fileName: string | undefined) => {
  if (!fileName) return false;

  const fileExtension = fileName.split('.').pop()?.toLocaleLowerCase();
  return fileExtension !== undefined && ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension);
};

export const isVideo = (fileName: string | undefined) => {
  if (!fileName) return false;

  const fileExtension = fileName.split('.').pop()?.toLocaleLowerCase();
  return fileExtension !== undefined && ['mp4'].includes(fileExtension);
};

export const containsImageOrVideo = (files: File[] | FileType[] | null | undefined) => {
  if (!files) return false;

  return files.some((file) => isImage(file.name) || isVideo(file.name));
};

export const isToday = (date: Date | string) => {
  const today = new Date();
  const dateToCheck = new Date(date);

  return (
    dateToCheck.getDate() === today.getDate() &&
    dateToCheck.getMonth() === today.getMonth() &&
    dateToCheck.getFullYear() === today.getFullYear()
  );
};

export const formatProgrammeDates = (from: Date | string | undefined | null, to: Date | string | undefined | null) => {
  if (!from || !to) return '';

  const fromDate = new Date(from);
  const fromDay = fromDate.getDate();
  const fromMonth = SHORT_MONTHS[fromDate.getMonth()]!;
  const fromYear = fromDate.getFullYear();

  const toDate = new Date(to);
  const toDay = toDate.getDate();
  const toMonth = SHORT_MONTHS[toDate.getMonth()]!;
  const toYear = toDate.getFullYear();

  let showFirstYear = false;
  if (fromYear !== toYear) showFirstYear = true;

  let showFirstMonth = false;
  if (fromMonth !== toMonth) showFirstMonth = true;

  return `${fromDay} ${showFirstMonth ? fromMonth : ''} ${showFirstYear ? fromYear : ''} -
    ${toDay} ${toMonth} ${toYear}`;
};

export const formatUsername = (username: string | null | undefined) => {
  if (!username) return '';

  if (username.includes('Deleted Account')) {
    return 'Deleted Account';
  }

  return username;
};

export const validateSouthAfricanIdNumber = (idNumber: string | null | undefined) => {
  if (!idNumber || idNumber.length !== 13) return false;

  const idYear = idNumber.substring(0, 2);
  const idMonth = idNumber.substring(2, 4);
  const idDay = idNumber.substring(4, 6);

  try {
    const date = new Date(parseInt(idYear, 10), parseInt(idMonth, 10) - 1, parseInt(idDay, 10));

    const dateYear = date.getFullYear().toString();

    let dateMonth: string | number = date.getMonth() + 1;
    if (dateMonth < 10) {
      dateMonth = `0${dateMonth}`;
    } else {
      dateMonth = dateMonth.toString();
    }

    let dateDay: string | number = date.getDate();
    if (dateDay < 10) {
      dateDay = `0${dateDay}`;
    } else {
      dateDay = dateDay.toString();
    }

    if (dateYear.slice(2, 4) !== idYear) return false;
    if (dateMonth !== idMonth) return false;
    if (dateDay !== idDay) return false;
  } catch (_error) {
    return false;
  }

  const gender = parseInt(idNumber.substring(6, 10));
  if (gender < 1 || gender > 9999) return false;

  const citizenship = idNumber.substring(10, 11);
  if (citizenship !== '0' && citizenship !== '1') return false;

  let checkSum = 0;
  for (let i = 0; i < idNumber.length; i++) {
    let value = parseInt(idNumber.charAt(i));
    if (i % 2 !== 0) {
      value *= 2;

      if (value > 9) {
        value -= 9;
      }
    }

    checkSum += value;
  }

  if (checkSum % 10 !== 0) return false;

  return true;
};

export const getWeekOptions = (
  programme: Programme | null | undefined,
  parentProgramme: ParentProgrammeGeneralDetails | null | undefined
) => {
  if (!programme && !parentProgramme) return [];

  const fromDate = new Date(programme?.fromDate ?? parentProgramme?.runningFromDate ?? '');
  const toDate = new Date(programme?.toDate ?? parentProgramme?.runningToDate ?? '');

  const daysInProgramme = (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24);
  const weeksInProgramme = Math.ceil(daysInProgramme / 7);

  const options = [];

  for (let i = 1; i <= weeksInProgramme; i++) {
    options.push({ label: i.toString(), value: i.toString() });
  }

  return options;
};
